import { action, computed, observable } from 'mobx';
import moment from 'moment';
import { BaseFilter, Entity } from '..';
import { Lang } from '../../enums';
import { ISerializable } from '../Entity';
import BannerButton from './BannerButton';
import BannerAudienceFilter from './BannerAudienceFilter';

export type BannerType = 'modal' | 'static';

export default class Banner extends Entity implements ISerializable {
    constructor(banner?: Partial<Banner>) {
        super();
        this.filter = new BannerAudienceFilter();
        if (banner) this.update(banner);
    }

    @observable name: string;
    @observable type: BannerType;
    @observable title: string;
    @observable text: string;
    @observable lang: Lang;
    @observable isPublished: boolean;
    @observable beginDate: moment.Moment | null;
    @observable endDate: moment.Moment;
    @observable maxShowsPerUser: number | null;
    @observable maxShowsPerDay: number | null;
    @observable minBreakBetweenShowsMinutes: number | null;
    @observable buttons: BannerButton[];
    @observable imageUrl: string | null;
    @observable staticHideable: boolean;
    @observable bannerLink: string | null;
    @observable createdDate: moment.Moment;
    @observable updatedDate: moment.Moment;
    @observable filter: BannerAudienceFilter;
    @observable billingPlanNames: string[] = [];

    @computed get isActive() {
        if (!this.isPublished) return false;
        const now = moment();
        if (this.beginDate && now.isBefore(this.beginDate)) return false;
        if (this.endDate && now.isAfter(this.endDate)) return false;
        return true;
    }

    toJson() {
        return {
            id: this.id,
            name: this.name,
            type: this.type,
            title: this.title,
            text: this.text,
            lang: this.lang,
            isPublished: this.isPublished,
            beginDate: this.beginDate?.toISOString() ?? null,
            endDate: this.endDate?.toISOString(),
            maxShowsPerUser: this.maxShowsPerUser,
            maxShowsPerDay: this.maxShowsPerDay,
            minBreakBetweenShowsMinutes: this.minBreakBetweenShowsMinutes,
            buttons: this.buttons?.map(b => b.toJson()) ?? [],
            imageUrl: this.imageUrl,
            staticHideable: this.staticHideable,
            bannerLink: this.bannerLink,
            filter: this.filter?.toJson() ?? new BannerAudienceFilter().toJson(),
        };
    }

    clone() {
        return new Banner({
            ...this,
            buttons: this.buttons?.map(b => b.clone()) ?? [],
            createdDate: this.createdDate ? moment(this.createdDate) : undefined,
            beginDate: this.beginDate ? moment(this.beginDate) : null,
            endDate: this.endDate ? moment(this.endDate) : undefined,
            updatedDate: this.updatedDate ? moment(this.updatedDate) : undefined,
            filter: this.filter?.clone() ?? new BannerAudienceFilter(),
            billingPlanNames: this.billingPlanNames ? [...this.billingPlanNames] : [],
        });
    }

    @action update(banner: Partial<Banner>, allowUndefined = false) {
        super.update(banner, allowUndefined);
    }

    static fromJson(json: any): Banner {
        return new Banner({
            ...json,
            buttons: json.buttons?.map((b: any) => BannerButton.fromJson(b)) ?? [],
            createdDate: json.createdDate ? moment(json.createdDate) : undefined,
            beginDate: json.beginDate ? moment(json.beginDate) : null,
            endDate: json.endDate ? moment(json.endDate) : undefined,
            updatedDate: json.updatedDate ? moment(json.updatedDate) : undefined,
            filter: Banner.parseFilter(json),
            billingPlanNames: Array.isArray(json.billingPlanNames) ? json.billingPlanNames : [],
        });
    }

    private static parseFilter(json: any): BannerAudienceFilter {
        if (json.filter && typeof json.filter === 'object') {
            return BannerAudienceFilter.fromJson(json.filter);
        }
        if (typeof json.filterJson === 'string') {
            return BannerAudienceFilter.deserialize(json.filterJson);
        }
        return new BannerAudienceFilter();
    }
}

export class BannerFilter extends BaseFilter<Banner> {
    constructor(filter?: Partial<BannerFilter>) {
        super();
        if (filter) this.update(filter);
    }

    id: string;
    excludeId: string;
    name: string;
    type: BannerType;
    isActive: boolean;
    isPublished: boolean;
    lang: Lang;

    update(changes: Partial<BannerFilter>) {
        super.update(changes);
    }
}
